import React, { useEffect, useState, useMemo } from "react";
import "./ServiceBody.css";
import {
  EmailValidation,
  PhoneNumberValidation,
  StringValidation,
} from "../../../Store/validate";
import { UserServiceRequest } from "../../../Api/Api";
import { showToastErrorMsg, showToastSuccessMsg } from "../../../Store/toastMsg";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";

export default function ServiceBanner({ contentProps = [], stateProps }) {
  const { slug } = useParams();
  const service = contentProps?.[0];

  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  /* ---------------- CONVERT SLUG TO TITLE CASE ---------------- */
  const slugToTitle = (text) => {
    if (!text) return "";
    return text
      .split("-")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  /* ---------------- INIT USER DATA ---------------- */
  useEffect(() => {
    window.scrollTo(0, 0);

    if (stateProps?.auth?.isLoggedIn) {
      setFormData({
        name: stateProps.auth.auth.name || "",
        email: stateProps.auth.auth.email || "",
        mobileNumber: stateProps.auth.auth.mobileNumber || "",
      });
    }
  }, [stateProps]);

  /* ---------------- HELPERS ---------------- */
  const setValue = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
    setErrors(prev => ({ ...prev, [key]: null }));
  };

  const validateField = (key, validator, value) => {
    setErrors(prev => ({ ...prev, [key]: validator(value) }));
  };

  /* ---------------- SERVICE NAME LOGIC ---------------- */
  const serviceName = useMemo(() => {
    return (
      service?.subServiceL2Id?.title ||
      service?.subServiceL1Id?.title ||
      slugToTitle(slug) ||
      ""
    );
  }, [service, slug]);

  /* ---------------- SUBMIT ---------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!service?.subServiceL2Id?.title) {
      showToastErrorMsg("Service details still loading. Please try again.");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        subject: formData.subject || "",
        question: formData.question || "",
        type: "service",
        servicesCategory: service.mainServiceId.title,
        servicesSubcategory: service.subServiceL1Id.title,
        servicesTitle: service.subServiceL2Id.title,
        ...(stateProps?.auth?.isLoggedIn
          ? { userId: stateProps.auth.auth._id }
          : {
              name: formData.name,
              email: formData.email,
              mobileNumber: formData.mobileNumber,
            }),
      };

      const res = await UserServiceRequest(payload);

      if (res?.statusCode === 200) {
        showToastSuccessMsg(res.message);
        setFormData({});
      } else {
        showToastErrorMsg(res?.message || "Submission failed");
      }
    } catch {
      showToastErrorMsg("Something went wrong");
    }

    setLoading(false);
  };

  /* ---------------- SEO VALUES ---------------- */
  const title = `${serviceName} | Dalmaf`;
  const description = `Claim your ${serviceName} effortlessly with Dalmaf. Partner with us for expert guidance and maximize your returns.`;

  const url =
    typeof window !== "undefined" ? window.location.href : "";

  /* ---------------- STRUCTURED DATA ---------------- */
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: serviceName,
    description,
    provider: {
      "@type": "Organization",
      name: "Dalmaf",
      url: typeof window !== "undefined" ? window.location.origin : "",
    },
    areaServed: {
      "@type": "Country",
      name: "India",
    },
  };

  /* ---------------- UI ---------------- */
  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href={url} />

        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={url} />

        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <section
        className="service-banner"
        style={{
          backgroundImage:
            'url("https://static.vecteezy.com/system/resources/previews/002/909/374/non_2x/luxury-abstract-banner-background-modern-geometric-shapes-an-d-gold-line-art-wallpaper-design-for-website-prints-cover-backdrop-wall-art-and-wall-decoration-vector.jpg")',
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="container py-5">
          <div className="row align-items-center">

            <div className="col-lg-8 mb-4 text-center text-lg-start">
              <h1 className="fw-bold">{serviceName}</h1>
              <p className="lead">
                Claim your <strong>{serviceName}</strong> effortlessly with Dalmaf.
                Partner with us for expert guidance and maximize your returns.
              </p>
            </div>

            <div className="col-lg-4">
              <div className="p-4 border rounded shadow bg-white">
                <h5 className="text-center mb-3">
                  REQUEST A CALL BACK
                </h5>

                <form onSubmit={handleSubmit} noValidate>

                  <input
                    className="form-control mb-2"
                    placeholder="Name"
                    value={formData.name || ""}
                    disabled={stateProps?.auth?.auth?.name}
                    onChange={(e) => setValue("name", e.target.value)}
                    onBlur={(e) =>
                      validateField("name", StringValidation, e.target.value)
                    }
                  />

                  <input
                    className="form-control mb-2"
                    placeholder="Mobile"
                    maxLength={10}
                    value={formData.mobileNumber || ""}
                    disabled={stateProps?.auth?.auth?.mobileNumber}
                    onChange={(e) =>
                      setValue("mobileNumber", e.target.value)
                    }
                    onBlur={(e) =>
                      validateField(
                        "mobileNumber",
                        PhoneNumberValidation,
                        e.target.value
                      )
                    }
                  />

                  <input
                    type="email"
                    className="form-control mb-2"
                    placeholder="Email"
                    value={formData.email || ""}
                    disabled={stateProps?.auth?.auth?.email}
                    onChange={(e) =>
                      setValue("email", e.target.value)
                    }
                    onBlur={(e) =>
                      validateField(
                        "email",
                        EmailValidation,
                        e.target.value
                      )
                    }
                  />

                  <textarea
                    rows="2"
                    className="form-control mb-3"
                    placeholder="Requirement"
                    value={formData.subject || ""}
                    onChange={(e) =>
                      setValue("subject", e.target.value)
                    }
                  />

                  <button
                    className="btn btn-primary w-100"
                    disabled={loading || !service}
                  >
                    {loading ? "Submitting..." : "Get Started"}
                  </button>

                </form>
              </div>
            </div>

          </div>
        </div>
      </section>
    </>
  );
}